import React, { useMemo, useState } from 'react';
import { ChevronDown, Radio } from 'lucide-react';
import { FinancialPlanShareView } from '../../types/financialPlanShare';
import LgcTimelineHero from './LgcTimelineHero';
import LgcSharedPlanDetails from './LgcSharedPlanDetails';
import { sharedPlanIntroMessage } from '../../utils/financialPlanShareCopy';
import '../../components/pm/pm-shared.css';
import '../../pages/OperationsHub.css';
import '../../pages/LifetimeGoalsCalculator.css';
import './LgcSharedPlanView.css';

const FUNDED_THRESHOLD = 99.5;

interface LgcSharedPlanViewProps {
  view: FinancialPlanShareView;
  updatedAt: number | null;
  ownerDisplayName?: string | null;
  live?: boolean;
}

function formatUpdatedAt(ms: number | null): string {
  if (!ms) return '';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const LgcSharedPlanView: React.FC<LgcSharedPlanViewProps> = ({
  view,
  updatedAt,
  ownerDisplayName = null,
  live = true,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const expensesMet = view.result.phasedAffordability
    ? view.result.phasedAffordability.planAchievable
    : view.result.gapAnnual <= 0;
  const retirementPct = view.timeline.retirementFundedPctOnPace;
  const retirementFunded = retirementPct >= FUNDED_THRESHOLD;

  const metaLine = useMemo(() => {
    const parts = [`Ages ${view.currentAge} → retirement ${view.timeline.retirementAge}`];
    if (updatedAt) {
      parts.push(`Updated ${formatUpdatedAt(updatedAt)}`);
    }
    return parts.join(' · ');
  }, [view.currentAge, view.timeline.retirementAge, updatedAt]);

  return (
    <div className="lgc-shared-view">
      <header className="lgc-shared-view__banner">
        <div className="lgc-shared-view__banner-text">
          <p className="lgc-shared-view__eyebrow">{sharedPlanIntroMessage(ownerDisplayName)}</p>
          <h1 className="lgc-shared-view__title">{view.planName}</h1>
          <p className="lgc-shared-view__meta">{metaLine}</p>
          <div className="lgc-hub-stats lgc-shared-view__stats">
            <span className={`ops-stat ${expensesMet ? 'ops-stat-done' : 'ops-stat-overdue'}`}>
              Expenses {expensesMet ? 'on track' : 'short'}
            </span>
            <span className={`ops-stat ${retirementFunded ? 'ops-stat-done' : 'ops-stat-overdue'}`}>
              Retirement {Math.round(retirementPct)}%
            </span>
          </div>
        </div>
        {live && (
          <div className="lgc-shared-view__live" title="Updates automatically when the plan owner makes changes">
            <Radio size={14} aria-hidden />
            Live
          </div>
        )}
      </header>

      <div className="lgc-shared-view__panel lgc-shared-view__panel--timeline">
        <LgcTimelineHero timeline={view.timeline} fullPage />
      </div>

      <div className="lgc-shared-view__details">
        <button
          type="button"
          className={`lgc-shared-view__details-toggle${detailsOpen ? ' is-open' : ''}`}
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((v) => !v)}
        >
          What&apos;s in this plan
          <ChevronDown size={14} aria-hidden />
        </button>
        {detailsOpen && (
          <div className="lgc-shared-view__details-body">
            {view.hasFullPlan ? (
              <LgcSharedPlanDetails plan={view.plan} />
            ) : (
              <p className="lgc-shared-view__legacy-note">
                Full plan details are available on live shares. Ask the owner to open Share and save
                again — updates sync automatically after that.
              </p>
            )}
          </div>
        )}
      </div>

      <p className="lgc-disclaimer lgc-shared-view__disclaimer">
        Educational planning tool only — not financial, tax, or investment advice.
      </p>
    </div>
  );
};

export default LgcSharedPlanView;
